// Copyright (c) 2025 Audere, All Rights Reserved

import { Box, Card, CardContent, CardMedia, IconButton, Typography } from "@mui/material";
import { blue, red } from "@mui/material/colors";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useTranslation } from "react-i18next";

import { theme } from "../../theme";
import { createStyleSheet, globalStyles } from "../utils/styles";
import { useCallback, useMemo } from "react";
import { getRDTName } from "../utils/string";
import imagePlaceholder from "../../assets/image_placeholder.png";
import { generatePath, useLocation, useNavigate, useParams } from "react-router";
import { CREATE_PROJECT_WITH_TEST_PATH, UPDATE_PROJECT_WITH_TEST_PATH } from "../utils/constants";
import { PROJECT_FLOW_QUERY_KEYS, PROJECT_FLOW_STEP_VALUES } from "../types/projects";
import type { TestItem as TestItemType, TestItemProps } from "../types/projects";

const TestItem = (props: TestItemProps) => {
  const { testItem, toggleTestSelection, isTestSelected } = props;
  const { t } = useTranslation("EvaluationSetup");
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const handleTestSelection = useCallback(
    (isSelected: boolean) => {
      toggleTestSelection(isSelected);
    },
    [toggleTestSelection]
  );
  const handleNavigate = useCallback(
    (testItem: TestItemType) => {
      const { projectUid, orgUid } = params;
      searchParams.set(PROJECT_FLOW_QUERY_KEYS.STEP, PROJECT_FLOW_STEP_VALUES.IMAGE_UPLOAD);
      const path = generatePath(
        projectUid ? UPDATE_PROJECT_WITH_TEST_PATH : CREATE_PROJECT_WITH_TEST_PATH,
        {
          orgUid: orgUid ?? "",
          projectUid: projectUid ?? "",
          testUid: testItem?.rdt_uid,
        }
      );
      navigate(`${path}?${searchParams.toString()}`);
    },
    [params, searchParams, navigate]
  );

  return (
    <Card
      onClick={() => !isTestSelected && handleTestSelection(!isTestSelected)}
      sx={{
        ...styles.testItemContainer,
        background: isTestSelected ? blue[50] : theme.palette.common.white,
        border: `1px solid ${isTestSelected ? theme.palette.primary.main : theme.palette.grey[400]}`,
      }}
    >
      {isTestSelected ? (
        <IconButton
          onClick={() => handleTestSelection(!isTestSelected)}
          sx={styles.removeIconButton}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      ) : (
        <></>
      )}
      <CardMedia
        component="img"
        image={testItem.example_image_url ?? imagePlaceholder}
        alt={getRDTName(testItem)}
        sx={styles.testImg}
      />
      <CardContent sx={styles.testContent}>
        <Box sx={styles.innerTestContent}>
          <Typography variant="body1bold">{getRDTName(testItem)}</Typography>
          <IconButton
            className="link-icon"
            size="small"
            sx={styles.linkIcon}
            onClick={() => {
              handleNavigate(testItem);
            }}
            data-testid="LinkIcon"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                {t("uploadImages")}
              </Typography>
              <CloudUploadIcon fontSize="small" />
            </Box>
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TestItem;

const styles = createStyleSheet({
  testItemContainer: {
    position: "relative",
    cursor: "pointer",
    maxWidth: theme.spacing(28),
    width: "100%",
    height: theme.spacing(43),
    borderRadius: "6px",
    boxShadow: "none",
    p: 2,
    overflow: "visible",
  },
  removeIconButton: {
    position: "absolute",
    top: -6,
    right: -8,
    background: red[600],
    color: "white",
    fontSize: 14,
    p: theme.spacing(0.4),
    "&:hover": { background: red[600] },
  },
  testImg: {
    maxWidth: theme.spacing(22),
    width: "100%",
    maxHeight: theme.spacing(25),
    height: "100%",
    objectFit: "contain",
    borderRadius: 1.5,
  },
  testContent: {
    ...globalStyles.gapTop,
    p: 0,
  },
  innerTestContent: {
    display: "flex",
    flexDirection: "column",
    alignItems:"center",
    gap: "16px",
  },
  linkIcon: {
    p: 0.5,
    color: theme.palette.primary.main,
    borderRadius: "20px",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: theme.palette.primary.main + "10",
      transform: "scale(1.05)",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
    },
  },
});
